// src/hooks/useSiteInfo.js
import { useState, useEffect } from 'react';
import { onValue } from 'firebase/database';
import { dbRef } from 'utils/dbRef';
import { getEffectiveOverride, isCurrentlyOpen } from 'utils/businessHours';
import { resolveOrderingAvailability } from 'utils/orderingAvailability';
import {
  canContinueCheckout,
  getGracePeriodRemaining,
} from 'utils/checkoutGrace';

/**
 * Custom hook to access site configuration settings
 * @returns {Object} Site configuration object
 */
export const useSiteInfo = () => {
  const [specialInstructionsEnabled, setSpecialInstructionsEnabled] =
    useState(false);
  const [shoppingEnabled, setShoppingEnabled] = useState(false);
  const [remoteMenuEnabled, setRemoteMenuEnabled] = useState(false);
  const [languageSwitcherEnabled, setLanguageSwitcherEnabled] = useState(true);
  const [businessHoursOverride, setBusinessHoursOverride] = useState(null);
  const [_overrideDurationMinutes, setOverrideDurationMinutes] = useState(null);
  const [overrideExpiration, setOverrideExpiration] = useState(null);
  const [overrideSetAt, setOverrideSetAt] = useState(null);
  // Bumped when a force-close grace window runs out, so the page re-renders
  // into the closed state without waiting for an unrelated update (#668).
  const [, setGraceTick] = useState(0);
  const [gracePeriodMinutes, setGracePeriodMinutes] = useState(15);
  const [businessHours, setBusinessHours] = useState(null);
  const [timezone, setTimezone] = useState(null);
  const [loading, setLoading] = useState(true);
  // Default enabled: scheduled orders are already live, so if the Firebase flag
  // is absent we keep them on rather than silently disabling the feature.
  const [scheduledOrdersEnabled, setScheduledOrdersEnabled] = useState(true);

  useEffect(() => {
    // Listen for special instructions enabled changes
    const specialInstructionsRef = dbRef(
      '/siteInfo/specialInstructionsEnabled',
    );
    const unsubscribeSpecialInstructions = onValue(
      specialInstructionsRef,
      (snapshot) => {
        if (snapshot.exists()) {
          setSpecialInstructionsEnabled(Boolean(snapshot.val()));
        }
        setLoading(false);
      },
    );

    // Listen for shopping enabled changes
    const shoppingRef = dbRef('/siteInfo/shoppingEnabled');
    const unsubscribeShopping = onValue(shoppingRef, (snapshot) => {
      if (snapshot.exists()) {
        setShoppingEnabled(Boolean(snapshot.val()));
      }
    });

    // Listen for remote menu enabled changes
    const remoteMenuRef = dbRef('/siteInfo/remoteMenuEnabled');
    const unsubscribeRemoteMenu = onValue(remoteMenuRef, (snapshot) => {
      if (snapshot.exists()) {
        setRemoteMenuEnabled(Boolean(snapshot.val()));
      }
    });

    // Listen for language switcher enabled changes
    const languageSwitcherRef = dbRef('/siteInfo/languageSwitcherEnabled');
    const unsubscribeLanguageSwitcher = onValue(
      languageSwitcherRef,
      (snapshot) => {
        if (snapshot.exists()) {
          setLanguageSwitcherEnabled(Boolean(snapshot.val()));
        } else {
          // Default to true if not set
          setLanguageSwitcherEnabled(true);
        }
      },
    );

    // Listen for business hours override
    const overrideRef = dbRef('/siteInfo/businessHoursOverride');
    const unsubscribeOverride = onValue(overrideRef, (snapshot) => {
      if (snapshot.exists()) {
        setBusinessHoursOverride(snapshot.val());
      } else {
        setBusinessHoursOverride(null);
      }
    });

    // Listen for override duration
    const overrideDurationRef = dbRef('/siteInfo/overrideDurationMinutes');
    const unsubscribeOverrideDuration = onValue(
      overrideDurationRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const value = Number(snapshot.val());
          setOverrideDurationMinutes(Number.isFinite(value) ? value : null);
        } else {
          setOverrideDurationMinutes(null);
        }
      },
    );

    // Listen for override expiration
    const expirationRef = dbRef('/siteInfo/overrideExpiration');
    const unsubscribeExpiration = onValue(expirationRef, (snapshot) => {
      if (snapshot.exists()) {
        setOverrideExpiration(snapshot.val());
      } else {
        setOverrideExpiration(null);
      }
    });

    // Listen for when the override was set (#668)
    const overrideSetAtRef = dbRef('/siteInfo/overrideSetAt');
    const unsubscribeOverrideSetAt = onValue(overrideSetAtRef, (snapshot) => {
      const value = snapshot.exists() ? Number(snapshot.val()) : null;
      setOverrideSetAt(Number.isFinite(value) ? value : null);
    });

    // Listen for grace period
    const gracePeriodRef = dbRef('/siteInfo/gracePeriodMinutes');
    const unsubscribeGracePeriod = onValue(gracePeriodRef, (snapshot) => {
      if (snapshot.exists()) {
        setGracePeriodMinutes(Number(snapshot.val()) || 15);
      }
    });

    // Listen for business hours
    const businessHoursRef = dbRef('/siteInfo/businessHours');
    const unsubscribeBusinessHours = onValue(businessHoursRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        // Extract hours from the stored data
        setBusinessHours(data?.hours || null);
      } else {
        setBusinessHours(null);
      }
    });

    // Listen for timezone setting
    const timezoneRef = dbRef('/siteInfo/timezone');
    const unsubscribeTimezone = onValue(timezoneRef, (snapshot) => {
      if (snapshot.exists()) {
        setTimezone(snapshot.val());
      } else {
        setTimezone(null);
      }
    });

    // Listen for scheduled orders enabled changes
    const scheduledOrdersRef = dbRef('/siteInfo/scheduledOrdersEnabled');
    const unsubscribeScheduledOrders = onValue(
      scheduledOrdersRef,
      (snapshot) => {
        if (snapshot.exists()) {
          setScheduledOrdersEnabled(Boolean(snapshot.val()));
        }
      },
    );

    return () => {
      unsubscribeSpecialInstructions();
      unsubscribeShopping();
      unsubscribeRemoteMenu();
      unsubscribeLanguageSwitcher();
      unsubscribeScheduledOrders();
      unsubscribeOverride();
      unsubscribeOverrideDuration();
      unsubscribeExpiration();
      unsubscribeOverrideSetAt();
      unsubscribeGracePeriod();
      unsubscribeBusinessHours();
      unsubscribeTimezone();
    };
  }, []);

  // Get effective override (considering expiration)
  const effectiveOverride = getEffectiveOverride(
    businessHoursOverride,
    overrideExpiration,
  );

  // Force close (#668). Indefinite: no ASAP and no scheduled orders. With a
  // duration: no ASAP, but pickups after it ends can still be scheduled, so
  // it behaves like an ordinary closure. In both cases a customer who was
  // already in checkout when the owner pressed the button orders against the
  // ordinary schedule for the few minutes after it, so they can finish.
  const forceClosed = effectiveOverride === 'closed';
  const forceCloseGrace =
    forceClosed && canContinueCheckout(effectiveOverride, overrideSetAt);
  const orderingOverride = forceCloseGrace ? null : effectiveOverride;

  useEffect(() => {
    if (!forceCloseGrace) return undefined;
    const timer = setTimeout(
      () => setGraceTick((tick) => tick + 1),
      getGracePeriodRemaining(overrideSetAt) + 50,
    );
    return () => clearTimeout(timer);
  }, [forceCloseGrace, overrideSetAt]);

  // Check if restaurant is currently closed
  const isClosed =
    businessHours &&
    !isCurrentlyOpen(
      businessHours,
      effectiveOverride,
      new Date(),
      gracePeriodMinutes,
      timezone,
    );

  // The same question as it applies to placing an order, which differs from
  // isClosed only inside the force-close grace window.
  const isClosedForOrdering = forceCloseGrace
    ? !isCurrentlyOpen(
        businessHours,
        orderingOverride,
        new Date(),
        gracePeriodMinutes,
        timezone,
      )
    : isClosed;

  // Auto-disable shopping when closed (respects grace period)
  const effectiveShoppingEnabled = shoppingEnabled && !isClosed;

  // Ordering and *immediate* ordering are different questions since #485:
  // while closed a customer may still book tomorrow.
  const { asapAvailable, orderingEnabled } = resolveOrderingAvailability({
    rawShoppingEnabled: shoppingEnabled,
    isClosed: businessHours ? isClosedForOrdering : null,
    scheduledOrdersEnabled,
    forceClosed: forceClosed && !overrideExpiration && !forceCloseGrace,
  });

  return {
    specialInstructionsEnabled,
    shoppingEnabled: effectiveShoppingEnabled, // Auto-disabled when closed!
    rawShoppingEnabled: shoppingEnabled, // Raw shopping enabled state
    asapAvailable, // The kitchen can cook now
    orderingEnabled, // Something can be ordered: now or scheduled (#485)
    remoteMenuEnabled,
    languageSwitcherEnabled,
    scheduledOrdersEnabled,
    businessHoursOverride: effectiveOverride, // Expiration-aware!
    orderingOverride, // What the pickup rule should judge against (#668)
    forceCloseGrace, // Mid-checkout customer may still finish (#668)
    overrideExpiration,
    overrideSetAt,
    gracePeriodMinutes,
    businessHours, // Real-time Clover hours
    timezone, // Restaurant's timezone from Clover
    loading,
    isClosed, // Expose for ClosedBanner
  };
};
